import React, { useEffect, useMemo, useState, useCallback } from "react";
import Select from "react-select";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import {
  isErpApiSuccess,
  listBankAccounts,
  listCustomerUsers,
  listSuppliers,
  postCashbook,
} from "../erpClient";
import { extractListRowsFromResponse } from "../utils/parseApiPagination";
import { buildJamaVoucherData } from "./JamaVoucherPDF";
import JamaVoucherPreviewModal from "./JamaVoucherPreviewModal";

/** Bank settlement: non-cash modes only */
const BANK_PAYMENT_MODE_OPTIONS = [
  { value: "UPI", label: "UPI" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
  { value: "CHEQUE", label: "Cheque" },
  { value: "CARD", label: "Card" },
  { value: "WALLET", label: "Wallet" },
];

function mapCustomerOption(item) {
  return {
    value: item.id,
    label: `${item.name || ""}${item.mobile ? ` (${item.mobile})` : ""}`,
    name: item.name || "",
    mobile: item.mobile || "",
    address: item.address || item.address_line || "",
    city: item.city || "",
    gst: item.gst_number || item.GST_number || item.gst || "",
  };
}

function mapSupplierOption(item) {
  const label = item.company_name || item.name || item.short_code || "";
  const partyNameForApi =
    String(item.name || "").trim() ||
    item.company_name ||
    item.short_code ||
    "";
  return {
    value: item.id,
    label,
    name: partyNameForApi,
    mobile: item.contact_number || item.mobile || "",
    address: item.address || item.address_line || "",
    city: item.city || "",
    gst: item.gst_number || item.GST_number || item.gst || "",
  };
}

function BankbookQuickPopup({
  show,
  onClose,
  bankFlowType = "bank-in",
  buttonClass = "",
  branchId = 1,
}) {
  const [activeCashTab, setActiveCashTab] = useState("customer");
  const [entryDate, setEntryDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [partySearch, setPartySearch] = useState("");
  const [partyOptions, setPartyOptions] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [summaryData, setSummaryData] = useState(null);
  const [loadingPartyOptions, setLoadingPartyOptions] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isPartyMenuOpen, setIsPartyMenuOpen] = useState(false);
  const [showVoucherPreview, setShowVoucherPreview] = useState(false);
  const [voucherData, setVoucherData] = useState(null);

  const txType = bankFlowType === "bank-out" ? "Bank_OUT" : "Bank_IN";
  const isBankIn = bankFlowType !== "bank-out";

  const getPartyTypeCode = () =>
    activeCashTab === "customer" ? "CUSTOMER" : "SUPPLIER";

  const loadBankAccounts = useCallback(async () => {
    setLoadingBanks(true);
    try {
      const response = await listBankAccounts(
        branchId != null ? { branch_id: branchId } : {},
      );
      if (response?.success) {
        const rows = extractListRowsFromResponse(response, ["bank_accounts"]);
        const opts = rows
          .filter(
            (bank) => String(bank.account_type || "").toLowerCase() !== "cash",
          )
          .map((bank) => ({
            value: bank.id,
            label: bank.account_number
              ? `${bank.bank_name || ""} — ${bank.account_number}`
              : bank.bank_name || `Account #${bank.id}`,
            data: bank,
          }));
        setBankAccounts(opts);
      } else {
        setBankAccounts([]);
      }
    } catch {
      setBankAccounts([]);
    } finally {
      setLoadingBanks(false);
    }
  }, [branchId]);

  useEffect(() => {
    if (!show) return;
    setActiveCashTab(bankFlowType === "bank-out" ? "supplier" : "customer");
    setEntryDate(new Date().toISOString().split("T")[0]);
    setPartySearch("");
    setPartyOptions([]);
    setSelectedParty(null);
    setAmount("");
    setPaymentMode(null);
    setSelectedBank(null);
    setRemarks("");
    setSummaryData(null);
    setIsPartyMenuOpen(false);
    loadBankAccounts();
  }, [show, bankFlowType, loadBankAccounts]);

  useEffect(() => {
    if (!show) return;
    setSelectedParty(null);
    setPartySearch("");
    setPartyOptions([]);
    setSummaryData(null);
    setIsPartyMenuOpen(false);
  }, [activeCashTab, show]);

  useEffect(() => {
    if (!show) return;
    const keyword = String(partySearch || "").trim();
    if (!keyword) {
      setPartyOptions([]);
      setIsPartyMenuOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoadingPartyOptions(true);
      try {
        if (activeCashTab === "customer") {
          const response = await listCustomerUsers({ search: keyword });
          const rows = extractListRowsFromResponse(response, ["customers"]);
          setPartyOptions(rows.map(mapCustomerOption));
          setIsPartyMenuOpen(true);
        } else {
          const response = await listSuppliers({ search: keyword, status: 1 });
          const rows = extractListRowsFromResponse(response, ["suppliers"]);
          setPartyOptions(
            rows
              .filter((item) =>
                String(item.company_name || item.name || "")
                  .toLowerCase()
                  .includes(keyword.toLowerCase()),
              )
              .map(mapSupplierOption),
          );
          setIsPartyMenuOpen(true);
        }
      } catch {
        setPartyOptions([]);
        setIsPartyMenuOpen(false);
      } finally {
        setLoadingPartyOptions(false);
      }
    }, 220);

    return () => clearTimeout(timer);
  }, [partySearch, activeCashTab, show]);

  const fetchPartyDueSummary = async (party) => {
    if (!party?.value) return;
    setLoadingSummary(true);
    try {
      const response = await postCashbook({
        type: "party_due_summary",
        party_type: getPartyTypeCode(),
        party_id: party.value,
        party_name: party.name || "",
        branch_id: branchId,
      });
      if (isErpApiSuccess(response) || response?.status === true) {
        const data = response?.data || null;
        setSummaryData(data);
        setAmount(
          data?.total_due_amount != null ? String(data.total_due_amount) : "",
        );
      } else {
        setSummaryData(null);
        toast.error(response?.message || "Unable to load due summary");
      }
    } catch (error) {
      setSummaryData(null);
      toast.error(error?.message || "Unable to load due summary");
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleSave = async ({ print = false } = {}) => {
    if (
      !selectedParty ||
      !amount ||
      Number(amount) <= 0 ||
      !paymentMode ||
      !selectedBank
    ) {
      return;
    }
    const idempotencyKey = `bank-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const amountToSave = Number(amount);
    const remarksToSave = remarks || "";
    const dateToSave = entryDate;
    const modeLabel = paymentMode.label || "Bank";
    const partySnapshot = { ...selectedParty };

    setSaving(true);
    try {
      const response = await postCashbook(
        {
          type: "bank_settlement",
          branch_id: branchId,
          idempotency_key: idempotencyKey,
          tx_type: txType,
          party_type: getPartyTypeCode(),
          party_id: selectedParty.value,
          party_name: selectedParty.name || "",
          amount: amountToSave,
          date: dateToSave,
          payment_mode: modeLabel,
          bank_account_id: selectedBank.value,
          remarks: remarksToSave,
        },
        { idempotencyKey },
      );
      if (isErpApiSuccess(response) || response?.status === true) {
        toast.success("Bank settlement saved successfully");

        if (print) {
          setVoucherData(
            buildJamaVoucherData({
              isIn: isBankIn,
              party: partySnapshot,
              amount: amountToSave,
              balance: summaryData?.total_due_amount,
              date: dateToSave,
              remarks: remarksToSave,
              paymentModeLabel: modeLabel,
              apiResponse: response,
            }),
          );
          setShowVoucherPreview(true);
          onClose?.();
        } else {
          setAmount("");
          setRemarks("");
          setPaymentMode(null);
          setSelectedBank(null);
          setEntryDate(new Date().toISOString().split("T")[0]);
          await fetchPartyDueSummary(selectedParty);
        }
      } else {
        toast.error(response?.message || "Failed to save bank settlement");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to save bank settlement");
    } finally {
      setSaving(false);
    }
  };

  const canSave = useMemo(
    () =>
      selectedParty &&
      amount !== "" &&
      Number(amount) > 0 &&
      paymentMode &&
      selectedBank,
    [selectedParty, amount, paymentMode, selectedBank],
  );

  const partySelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: 44,
      height: 44,
      borderRadius: 10,
      borderColor: "#dce3ea",
      boxShadow: "none",
      "&:hover": { borderColor: "#c7d0db" },
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: 44,
      padding: "0 10px",
    }),
    input: (provided) => ({ ...provided, margin: 0, padding: 0 }),
    menu: (provided) => ({ ...provided, zIndex: 2000, borderRadius: 10 }),
    menuList: (provided) => ({ ...provided, maxHeight: 240 }),
    option: (base, state) => {
      const active = Boolean(state.isFocused || state.isSelected);
      return {
        ...base,
        backgroundColor: active
          ? "var(--theme-primary, #000000)"
          : "#ffffff",
        color: active ? "#ffffff" : "var(--theme-text-primary, #212529)",
        cursor: "pointer",
        ":active": {
          ...base[":active"],
          backgroundColor: "var(--theme-primary, #000000)",
          color: "#ffffff",
        },
      };
    },
  };

  const bankSelectStyles = {
    ...partySelectStyles,
    control: (provided) => ({
      ...partySelectStyles.control(provided),
      minHeight: 38,
      height: "auto",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0 10px",
      minHeight: 38,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "var(--theme-primary)"
        : state.isFocused
          ? "rgba(var(--bs-primary-rgb, 59, 130, 246), 0.1)"
          : "#ffffff",
      color: state.isSelected ? "#ffffff" : "var(--bs-heading-color)",
      fontWeight: state.isSelected ? 600 : 400,
      cursor: "pointer",
      ":active": {
        backgroundColor: "var(--theme-primary)",
        color: "#ffffff",
      },
    }),
  };

  if (!show && !showVoucherPreview) return null;

  return (
    <>
      <Modal show={show} onHide={onClose} centered size="md">
        <Modal.Header closeButton>
          <Modal.Title>
            {bankFlowType === "bank-out" ? "Bank-OUT" : "Bank-IN"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="d-flex gap-2 mb-3">
            <button
              type="button"
              className={`btn flex-fill ${activeCashTab === "customer" ? "btn-primary" : "btn-light border"} ${buttonClass}`}
              onClick={() => setActiveCashTab("customer")}
            >
              Customer
            </button>
            <button
              type="button"
              className={`btn flex-fill ${activeCashTab === "supplier" ? "btn-primary" : "btn-light border"} ${buttonClass}`}
              onClick={() => setActiveCashTab("supplier")}
            >
              Supplier/Karigar
            </button>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <label className="form-label fw-semibold mb-0">Party Name <span className="text-danger">*</span></label>
                {selectedParty && (
                  <span
                    className="badge bg-danger-subtle text-danger"
                    style={{ fontWeight: 700 }}
                  >
                    Due:{" "}
                    {loadingSummary
                      ? "..."
                      : (summaryData?.total_due_amount ?? 0)}
                  </span>
                )}
              </div>
              <Select
                className="w-100"
                classNamePrefix="party-select"
                styles={partySelectStyles}
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                  ClearIndicator: () => null,
                }}
                options={partyOptions}
                value={selectedParty}
                inputValue={partySearch}
                onInputChange={(value, actionMeta) => {
                  if (actionMeta.action === "input-change")
                    setPartySearch(value);
                }}
                onChange={(option) => {
                  setSelectedParty(option);
                  setPartySearch("");
                  setIsPartyMenuOpen(false);
                  if (option) fetchPartyDueSummary(option);
                  else setSummaryData(null);
                }}
                onKeyDown={(event) => {
                  if (
                    event.key === "ArrowDown" &&
                    partyOptions.length > 0 &&
                    !isPartyMenuOpen
                  ) {
                    event.preventDefault();
                    setIsPartyMenuOpen(true);
                  }
                }}
                menuIsOpen={isPartyMenuOpen}
                onMenuOpen={() => setIsPartyMenuOpen(true)}
                onMenuClose={() => setIsPartyMenuOpen(false)}
                isLoading={loadingPartyOptions}
                openMenuOnFocus
                placeholder={
                  activeCashTab === "customer"
                    ? "Enter customer name or mobile"
                    : "Enter supplier/karigar name"
                }
                noOptionsMessage={() => "No matching records"}
                isClearable
              />
            </div>
          </div>

          <div className="row g-3 align-items-stretch">
            <div className="col-12">
              <div className="rounded-3 border p-3 h-100">
                <h6 className="fw-semibold mb-3">Bank Entry</h6>
                <div className="mb-3">
                  <label className="form-label fw-semibold mb-1">Amount <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    min="0"
                    className={`form-control ${buttonClass}`}
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold mb-1">Date <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={entryDate}
                    onChange={(e) => setEntryDate(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold mb-1">Mode <span className="text-danger">*</span></label>
                  <Select
                    styles={bankSelectStyles}
                    options={BANK_PAYMENT_MODE_OPTIONS}
                    value={paymentMode}
                    onChange={setPaymentMode}
                    placeholder="Select mode"
                    isClearable
                    classNamePrefix="bank-select"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Bank account <span className="text-danger">*</span>
                  </label>
                  <Select
                    styles={bankSelectStyles}
                    options={bankAccounts}
                    value={selectedBank}
                    onChange={setSelectedBank}
                    isLoading={loadingBanks}
                    placeholder="Select account"
                    isClearable
                    classNamePrefix="bank-select"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold mb-1">Remarks</label>
                  <textarea
                    rows={2}
                    className={`form-control ${buttonClass}`}
                    placeholder="Enter remarks"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                </div>
                <div className="d-flex justify-content-end gap-2 pt-1 flex-wrap">
                  <button
                    type="button"
                    className={`btn btn-primary ${buttonClass}`}
                    onClick={() => handleSave({ print: false })}
                    disabled={saving || !canSave}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-outline-primary ${buttonClass}`}
                    onClick={() => handleSave({ print: true })}
                    disabled={saving || !canSave}
                  >
                    {saving ? "Saving..." : "Save & Print"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <JamaVoucherPreviewModal
        show={showVoucherPreview}
        onHide={() => {
          setShowVoucherPreview(false);
          setVoucherData(null);
        }}
        voucherData={voucherData}
      />
    </>
  );
}

export default BankbookQuickPopup;
